import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, ListTree } from 'lucide-react';
import { diagnosticsAPI, entitiesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * A&L Invoice Drilldown (Phase A inspection tooling).
 *
 * Per-invoice listing behind the "BR OS client invoices" and "Unpaid
 * invoices" A&L receivables lines for one entity, so an operator can
 * eyeball exactly which invoices make up a headline count. possible_bros_twin
 * is an informational normalized-number heuristic only -- never proof of a
 * duplicate (see the panel's own note text, sourced from the backend).
 */
function fmt(n) {
  if (n === null || n === undefined) return '-';
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AlInvoiceDrilldownPanel() {
  var [entities, setEntities] = useState([]);
  var [selectedId, setSelectedId] = useState('br_ltd');
  var [loading, setLoading] = useState(false);
  var [result, setResult] = useState(null);

  useEffect(function () {
    entitiesAPI.getAll().then(function (res) {
      var list = res.data || [];
      list.sort(function (a, b) { return (a.name || '').localeCompare(b.name || ''); });
      setEntities(list);
    }).catch(function () {});
  }, []);

  var run = async function () {
    if (!selectedId) return;
    setLoading(true);
    setResult(null);
    try {
      var res = await diagnosticsAPI.alInvoiceDrilldown(selectedId);
      setResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var bros = result?.bros_client_invoices;
  var unpaid = result?.unpaid_invoices;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ListTree className="h-4 w-4" />
          A&amp;L Invoice Drilldown
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Per-invoice listing behind "BR OS client invoices" and "Unpaid invoices".
          possible_bros_twin is a normalized invoice-number match against bros_invoices
          -- informational only, never a dedup decision (the equivalent number-based
          join was retired after producing 46 wrong links in production).
        </p>
        <div className="flex items-center gap-2">
          <select
            className="text-sm border rounded px-2 py-1.5 bg-background flex-1"
            value={selectedId}
            onChange={function (e) { setSelectedId(e.target.value); }}
          >
            <option value="">Select entity...</option>
            {entities.map(function (ent) {
              return <option key={ent.id} value={ent.id}>{ent.name}</option>;
            })}
          </select>
          <Button size="sm" onClick={run} disabled={loading || !selectedId}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run'}
          </Button>
        </div>

        {result && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{result.entity_name}</span>
              <Badge variant="outline" className="font-mono text-xs">{result.entity_id}</Badge>
            </div>

            {/* BR OS client invoices */}
            {bros?.applicable && (
              <div>
                <h4 className="font-semibold mb-1">BR OS client invoices ({bros.count})</h4>
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="text-xs w-full border-collapse">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b text-left">
                        <th className="py-1 pr-2">number</th>
                        <th className="py-1 pr-2">counterparty</th>
                        <th className="py-1 pr-2">issue</th>
                        <th className="py-1 pr-2">due</th>
                        <th className="py-1 pr-2">status</th>
                        <th className="py-1 pr-2">ccy</th>
                        <th className="py-1 pr-2 text-right">total</th>
                        <th className="py-1 pr-2 text-right">paid</th>
                        <th className="py-1 pr-2 text-right">outstanding</th>
                        <th className="py-1 pr-2">counts?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bros.rows.map(function (r) {
                        return (
                          <tr key={r.id} className="border-b border-border/30">
                            <td className="py-1 pr-2 font-mono">{r.invoice_number}</td>
                            <td className="py-1 pr-2 truncate max-w-[140px]" title={r.counterparty}>{r.counterparty}</td>
                            <td className="py-1 pr-2 font-mono">{(r.issue_date || '').slice(0, 10) || '-'}</td>
                            <td className="py-1 pr-2 font-mono">{(r.due_date || '').slice(0, 10) || '-'}</td>
                            <td className="py-1 pr-2 font-mono">{r.status}</td>
                            <td className="py-1 pr-2 font-mono">{r.currency}</td>
                            <td className="py-1 pr-2 font-mono text-right">{fmt(r.total_amount)}</td>
                            <td className="py-1 pr-2 font-mono text-right">{fmt(r.amount_paid)}</td>
                            <td className="py-1 pr-2 font-mono text-right">{fmt(r.outstanding)}</td>
                            <td className="py-1 pr-2">
                              {r.counts_as_receivable
                                ? <Badge variant="default" className="text-[10px]">yes</Badge>
                                : <Badge variant="outline" className="text-[10px] text-muted-foreground">no</Badge>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Unpaid invoices */}
            <div>
              <h4 className="font-semibold mb-1 flex items-center gap-2">
                Unpaid invoices ({unpaid.count}, {unpaid.receivable_count} receivable)
                {unpaid.possible_bros_twin_count > 0 && (
                  <Badge variant="destructive" className="text-[10px]">
                    {unpaid.possible_bros_twin_count} possible BR OS twin{unpaid.possible_bros_twin_count === 1 ? '' : 's'}
                  </Badge>
                )}
              </h4>
              {Object.keys(unpaid.by_source || {}).length > 0 && (
                <div className="text-xs mb-2">
                  by source:{' '}
                  {Object.entries(unpaid.by_source).map(function (e) {
                    return <span key={e[0]} className="font-mono mr-2">{e[0]}={e[1]}</span>;
                  })}
                </div>
              )}
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="text-xs w-full border-collapse">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">number</th>
                      <th className="py-1 pr-2">source</th>
                      <th className="py-1 pr-2">counterparty</th>
                      <th className="py-1 pr-2">issue</th>
                      <th className="py-1 pr-2 text-right">amount</th>
                      <th className="py-1 pr-2">ccy</th>
                      <th className="py-1 pr-2">payment status</th>
                      <th className="py-1 pr-2">counts?</th>
                      <th className="py-1 pr-2">BR OS twin?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unpaid.rows.map(function (r) {
                      return (
                        <tr key={r.id} className={'border-b border-border/30' + (r.possible_bros_twin ? ' bg-amber-50 dark:bg-amber-950/30' : '')}>
                          <td className="py-1 pr-2 font-mono">{r.invoice_number || '-'}</td>
                          <td className="py-1 pr-2 font-mono">{r.source}</td>
                          <td className="py-1 pr-2 truncate max-w-[140px]" title={r.counterparty}>{r.counterparty}</td>
                          <td className="py-1 pr-2 font-mono">{(r.issue_date || '').slice(0, 10) || '-'}</td>
                          <td className="py-1 pr-2 font-mono text-right">{fmt(r.amount)}</td>
                          <td className="py-1 pr-2 font-mono">{r.currency}</td>
                          <td className="py-1 pr-2 font-mono">{r.payment_status || '-'}</td>
                          <td className="py-1 pr-2">
                            {r.counts_as_receivable
                              ? <Badge variant="default" className="text-[10px]">yes</Badge>
                              : <Badge variant="outline" className="text-[10px] text-muted-foreground">{r.classification}</Badge>}
                          </td>
                          <td className="py-1 pr-2">
                            {r.possible_bros_twin
                              ? <Badge variant="destructive" className="text-[10px]">possible</Badge>
                              : <span className="text-muted-foreground text-[10px]">-</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {unpaid.rows.length === 0 && (
                      <tr><td colSpan={9} className="py-2 text-muted-foreground text-center">no rows</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {result.note && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded p-2 text-xs">
                {result.note}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
